import { useState } from "react";
import { Box, CircularProgress, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetOne, useNotify, useRefresh } from "react-admin";
import { ResourceName, AlertType, type TaskTemplate } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { SideDrawer } from "@/components/common/SideDrawer";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ColorType } from "@/types/shared/uiComponents";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { TemplateInfoSection } from "./TemplateInfoSection";
import { BusinessLogicSection } from "./BusinessLogicSection";
import { NotificationSettingsSection } from "./NotificationSettingsSection";
import { UsageStatsSection } from "./UsageStatsSection";
import { MetadataSection } from "./MetadataSection";

interface TaskTemplateDrawerProps {
  templateId: string | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DRAWER_WIDTH = 520;

export { DRAWER_WIDTH as TASK_TEMPLATE_DRAWER_WIDTH };

export const TaskTemplateDrawer = ({ templateId, onClose, onDeleted }: TaskTemplateDrawerProps) => {
  const { canDelete, canEdit } = useResourcePermissions(ResourceName.TASK_TEMPLATES);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: template, isPending } = useGetOne<TaskTemplate>(
    ResourceName.TASK_TEMPLATES,
    { id: templateId! },
    { enabled: !!templateId }
  );

  const handleDelete = async () => {
    if (!templateId) return;
    setIsDeleting(true);
    const apiUrl = getPartnerAwareApiUrl();

    try {
      await httpClient(`${apiUrl}/${ResourceName.TASK_TEMPLATES}/${templateId}`, { method: "DELETE" });
      notify("Template deleted", { type: AlertType.SUCCESS });
      setConfirmDeleteOpen(false);
      refresh();
      onDeleted();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SideDrawer
        title={template?.title ?? "Template Details"}
        open={!!templateId}
        onClose={onClose}
        width={DRAWER_WIDTH}
        hideHandle
      >
        {isPending || !template ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box key={template.id} pb={4}>
            {/* Top actions */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              {canEdit ? (
                <Typography variant="caption" color="text.secondary">
                  Editable fields save on blur.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Read-only access.
                </Typography>
              )}
              {canDelete && (
                <Tooltip title="Delete template">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <DrawerSection title="Overview">
              <TemplateInfoSection template={template} />
            </DrawerSection>

            <DrawerSection title="Automation Rule">
              <BusinessLogicSection template={template} />
            </DrawerSection>

            <DrawerSection title="Notifications">
              <NotificationSettingsSection template={template} />
            </DrawerSection>

            <DrawerSection title="Usage">
              <UsageStatsSection templateId={template.id} />
            </DrawerSection>

            <DrawerSection title="Metadata">
              <MetadataSection metadata={template.metadata} />
            </DrawerSection>
          </Box>
        )}
      </SideDrawer>

      <ConfirmationDialog
        open={confirmDeleteOpen}
        title="Delete Template"
        message={`Are you sure you want to delete "${template?.title}"? This will prevent any future tasks from being auto-created by this template. Existing tasks are not affected.`}
        confirmLabel={"Delete" as never}
        confirmColor={ColorType.Error}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </>
  );
};

/**
 * Section wrapper with title, description, and divider.
 */
const DrawerSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.4 }}>
        {description}
      </Typography>
    )}
    {children}
    <Divider sx={{ mt: 2.5 }} />
  </Box>
);
